import React, { useState, useEffect, memo } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { Link } from "react-router-dom";
import { NewCreditorGroup } from "./Components";
import { creditorGroup } from "us.collection.admin/actions";
import { INavigationData, IRootState } from "us.collection/interfaces";
import { ManageCreditorInformation } from "us.collection.creditor/components/ManageCreditors";
import { useLocation } from "react-router-dom";
import { DrawerType, initialDrawerData } from "us.collection.admin/components/CreditorGroup/Constants";
import {
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CopyOutlined,
} from "us.icons";
import { ICreditorGroupHome } from "./Types";
import { CopyToCreditorBMD } from "./Components";
import './CreditorGroup.scss';

const {
  $Popover,
  $Popconfirm,
  $Button,
  $Divider,
  $PageHeader,
  $Drawer,
  $Affix,
  $Skeleton,
  $TableTree,
} = Common.Components;

const CreditorGroupHome: React.FC<ICreditorGroupHome & PropsFromRedux> = memo((props) => {
  const { t } = useTranslation();
  const { state } = useLocation();

  const {
    getCreditorGroups,
    creditorGroups,
    manageCreditorGroupDrawer,
    drawer,
    selectCreditor,
    resetSelectedCreditor,
    deleteCreditorGroup,
    onMinimize,
    isCreditorManagement,
    drawerContentChangeClick,
  } = props;

  const [popVisible, setPopVisibile] = useState<number>(-1);
  const [drawerInfo, setDrawerInfo] = useState<any>(initialDrawerData);

  useEffect(() => {
    getCreditorGroups && getCreditorGroups({});
  }, []);

  const onCloseAddNewCreditorDrawer = () => {
    setDrawerInfo(initialDrawerData);
  };

  // Popover actions in table
  const content = (record: any) => (
    <div className="table-more-content">
      <div
        className="d-flex flex-row more-item"
        onClick={() => {
          setPopVisibile(-1);
          editCreditorGroupHandler(record);
        }}
      >
        <div className="p-1">
          <EditOutlined />
        </div>
        <div className="p-1">{t("US.COLLECTION.COMMON:COMMON.EDIT")}</div>
      </div>
      <$Popconfirm
        title={t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.DELETECONFIRM")}
        onConfirm={() => {
          setPopVisibile(-1);
          deleteCreditorGroupHandler(record);
        }}
        onCancel={() => setPopVisibile(-1)}
      >
        <div className="d-flex flex-row more-item text-error">
          <div className="p-1">
            <DeleteOutlined />
          </div>
          <div className="p-1">{t("US.COLLECTION.COMMON:COMMON.DELETE")}</div>
        </div>
      </$Popconfirm>
      <div
        className="d-flex flex-row more-item"
        onClick={() => {
          setPopVisibile(-1);
          copyToCreditorGroupHandler(record);
        }}
      >
        <div className="p-1">
          <CopyOutlined />
        </div>
        <div className="p-1">{t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.COPY_BMD_TO")}</div>
      </div>
      <div
        className="d-flex flex-row more-item"
        onClick={() => {
          setPopVisibile(-1);
          setDrawerInfo({
            title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.NEW_CREDITOR"),
            visibility: true,
            groupData: record,
          });
        }}
      >
        <div className="p-1">
          <PlusOutlined />
        </div>
        <div className="p-1">
          {t("US.COLLECTION.COMMON:COMMON.NEW_CREDITOR")}
        </div>
      </div>
    </div>
  );

  const columns: any = [
    {
      title: "",
      key: "more",
      dataIndex: "more",
      width: 50,

      customRenderChild: (text: any, record: any) => {
        return (
          <$Popover
            trigger="click"
            placement="rightTop"
            content={content(record)}
            destroyTooltipOnHide
            visible={record.id == popVisible && !drawerInfo?.visibility}
            onVisibleChange={(visible: boolean) => {
              setPopVisibile(visible ? record.id : -1);
            }}
          >
            <$Button icon={<MoreOutlined />} size="small" />
          </$Popover>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.NO"),
      dataIndex: "id",
      key: "id",
      width: 120,
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
      customRenderChild: (text: any, record: any) => {
        const { invoiceNumber, kid } = record;
        return (
          <div>
            {!isCreditorManagement && (
              <Link
                to={{
                  pathname: `/creditor-group/${record?.id}`,
                  state: {
                    ...(state as INavigationData),
                    currentTab: "creditorgroup",
                  },
                }}
              >
                {text > 0 ? text : ""}
              </Link>
            )}
            {isCreditorManagement && text > 0 ? text : ""}
          </div>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.NAME"),
      dataIndex: "groupName",
      key: "groupName",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: true,
      width: "30%"
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.NUMBEROFCREDITORS"),
      dataIndex: "numberOfCreditors",
      key: "numberOfCreditors",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
      ellipsis: true,
      width: 175,
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.DESCRIPTION"),
      dataIndex: "description",
      key: "description",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      ellipsis: true,
      width: "70%"
    },
  ];

  const addNewCreditorGroupHandler = () => {
    resetSelectedCreditor && resetSelectedCreditor({});
    manageCreditorGroupDrawer && manageCreditorGroupDrawer({
      title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.ADDNEWCREDITORGROUP"),
      visible: true,
      isNewCreditorGroup: true,
      type: DrawerType.NEW
    });
  };

  const editCreditorGroupHandler = (record: any) => {
    selectCreditor && selectCreditor(record);
    manageCreditorGroupDrawer && manageCreditorGroupDrawer({
      title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.EDIT_CREDITOR_GROUP"),
      visible: true,
      isNewCreditorGroup: false,
      type: DrawerType.EDIT
    });
  };

  const copyToCreditorGroupHandler = (record: any) => {
    selectCreditor && selectCreditor(record);
    manageCreditorGroupDrawer && manageCreditorGroupDrawer({
      title: t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.COPY_BMD_TO_CREDITOR_GROUP"),
      visible: true,
      isNewCreditorGroup: false,
      type: DrawerType.COPY_BMD
    });
  };

  const deleteCreditorGroupHandler = (record: any) => {
    deleteCreditorGroup && deleteCreditorGroup(record?.id);
  };

  const onClose = () => {
    resetSelectedCreditor && resetSelectedCreditor({});
    manageCreditorGroupDrawer && manageCreditorGroupDrawer({
      title: "",
      visible: false,
      isNewCreditorGroup: true,
      type: ""
    });
  };


  const rowDoubleClickHandler = (selectedGroupData: any, rowIndex: any) => {
    drawerContentChangeClick({
      selectedGroupData,
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{}}
      onSubmit={(values: any, actions: any) => {}}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        ...rest
      }: any) => (
        <>
          <div className="space-content">
            {!isCreditorManagement && (
              <$Affix offsetTop={80}>
                <div className="page-header header-border">
                  <div className="d-flex flex-row align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <$PageHeader
                        className="px-0"
                        title={t(
                          "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.MANAGECREDITORGROUPS"
                        )}
                        onBack={() => onMinimize && onMinimize(state)}
                      />
                      <$Divider className="bui-devider" type="vertical" />
                      <$Skeleton
                        loading={creditorGroups?.isFetching}
                        active
                        paragraph={{ rows: 0 }}
                      >
                        <$Button
                          onClick={() => addNewCreditorGroupHandler()}
                          type="dashed"
                          size="small"
                          icon={<PlusOutlined />}
                        >
                          {t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.ADDNEW")}
                        </$Button>
                      </$Skeleton>
                    </div>
                  </div>
                </div>
              </$Affix>
            )}

            <div className="mt-3">
              <InfoCircleOutlined className="mr-2" />
              {t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.INFO")}
            </div>

            <$Skeleton
              loading={creditorGroups?.isFetching}
              active
              paragraph={{ rows: 2 }}
            >
              <$TableTree
                rowKey="arItemNo"
                data={creditorGroups?.data}
                size="small"
                className="mt-3"
                onSort={(sortData, dataSource) => sortData(dataSource)}
                onFilter={(searchData, dataSource) => searchData(dataSource)}
                filterOnType={true}
                resetOnSourceChange={true}
                bordered
                pagination={{ defaultPageSize: 200 }}
                scroll={{ x: 1200, y: "calc(100vh - 350px)" }}
                columns={
                  !isCreditorManagement
                    ? columns
                    : columns.filter((col: any) => col.dataIndex !== "more")
                }
                onRow={(record: any, rowIndex: any) => {
                  return {
                    onDoubleClick: (event: any) => {
                      if (isCreditorManagement) {
                        rowDoubleClickHandler(record, rowIndex);
                      } else {
                        editCreditorGroupHandler(record);
                      }
                    },
                  };
                }}
              />
            </$Skeleton>
          </div>

          <$Drawer
            title={drawer?.title}
            width={500}
            visible={drawer?.visible}
            onClose={onClose}
            destroyOnClose
          >
            {[DrawerType.NEW, DrawerType.EDIT].includes(drawer?.type) && (
              <NewCreditorGroup onClose={onClose} />
            )}
            {drawer?.type == DrawerType.COPY_BMD && (
              <CopyToCreditorBMD onClose={onClose}/>
            )}
          </$Drawer>

          <$Drawer
            title={drawerInfo?.title}
            width={"80%"}
            visible={drawerInfo?.visibility}
            onClose={onCloseAddNewCreditorDrawer}
            destroyOnClose
          >
            <ManageCreditorInformation
              isDrawer={true}
              isGroupDisabled={true}
              creditorGroupData={drawerInfo?.groupData}
              onClose={onCloseAddNewCreditorDrawer}
            />
          </$Drawer>
        </>
      )}
    </Formik>
  );
});

const mapStateToProps = (state: IRootState) => {
  const { common, creditorGroup } = state;
  const { currentCurrency, currentLanguage, currentDateFormat } = common;
  const { creditorGroups, drawer } = creditorGroup;

  return {
    currentCurrency,
    currentLanguage,
    currentDateFormat,
    creditorGroups,
    drawer,
  };
};

const mapDispatchToProps = {
  getCreditorGroups: creditorGroup.creditorGroups.get,
  manageCreditorGroupDrawer: creditorGroup.drawer.set,
  selectCreditor: creditorGroup.creditorGroup.select,
  resetSelectedCreditor: creditorGroup.creditorGroup.reset,
  deleteCreditorGroup: creditorGroup.creditorGroup.delete,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CreditorGroupHome);